import config from 'appConfig';
import { enqueueOaiq, isStoredConsentGranted } from './sdk';

export type OpenAIUserInput = {
  email?: string | null;
  phone?: string | null;
  externalId?: string | number | null;
  firstName?: string | null;
  lastName?: string | null;
  country?: string | null;
  city?: string | null;
  region?: string | null;
  postalCode?: string | null;
};

type PixelUser = Record<string, string>;

const DEFAULT_CALLING_CODE = '65';
const STORAGE_KEY = 'yb_oai_user';

const HASH_KEYS = [
  'email_sha256',
  'phone_number_sha256',
  'external_id_sha256',
  'first_name_sha256',
  'last_name_sha256',
] as const;

const PLAIN_KEYS = [
  'country',
  'city',
  'region',
  'postal_code',
] as const;

const normalizeEmail = (
  email?: string | null,
): string | undefined => {
  if (!email) return undefined;
  const normalized = email.trim().toLowerCase();
  return normalized.includes('@') ? normalized : undefined;
};

const normalizePhone = (
  phone?: string | null,
  defaultCode: string = DEFAULT_CALLING_CODE,
): string | undefined => {
  if (!phone) return undefined;

  let normalized = phone.trim().replace(/[\s().-]/g, '');
  if (normalized.startsWith('00')) {
    normalized = normalized.slice(2);
  } else {
    normalized = normalized.replace(/^\+/, '');
  }

  if (!/^\d+$/.test(normalized)) return undefined;

  if (defaultCode === DEFAULT_CALLING_CODE) {
    // Remove duplicated calling code, e.g. 6565854xxx
    while (normalized.startsWith('6565')) {
      normalized = '65' + normalized.slice(4);
    }

    if (normalized.startsWith('0')) {
      normalized = normalized.slice(1);
    }

    if (!normalized.startsWith('65')) {
      normalized = '65' + normalized;
    }
  } else {
    if (normalized.startsWith('0')) {
      normalized = normalized.slice(1);
    }

    if (!normalized.startsWith(defaultCode)) {
      normalized = defaultCode + normalized;
    }
  }

  return normalized.length >= 8 && normalized.length <= 15
    ? normalized
    : undefined;
};

const normalizeExternalId = (
  externalId?: string | number | null,
): string | undefined => {
  if (!externalId) return undefined;

  return String(externalId).trim();
};

const normalizeName = (name?: string | null): string | undefined => {
  if (!name) return undefined;

  return name.toLowerCase().replace(/[\s!-/:-@[-`{-~]/g, '');
};

const sha256Hex = async (
  value: string,
): Promise<string | undefined> => {
  try {
    const subtle =
      typeof globalThis !== 'undefined'
        ? globalThis.crypto?.subtle
        : undefined;
    if (!subtle || typeof TextEncoder === 'undefined')
      return undefined;

    const bytes = new TextEncoder().encode(value);
    const digest = await subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    return undefined;
  }
};

export const buildUser = async (
  input: OpenAIUserInput,
): Promise<PixelUser> => {
  const user: PixelUser = {};

  const hashed: [string, string | undefined][] = [
    ['email_sha256', normalizeEmail(input.email)],
    ['phone_number_sha256', normalizePhone(input.phone)],
    ['external_id_sha256', normalizeExternalId(input.externalId)],
    ['first_name_sha256', normalizeName(input.firstName)],
    ['last_name_sha256', normalizeName(input.lastName)],
  ];

  for (const [key, value] of hashed) {
    if (!value) continue;
    const hash = await sha256Hex(value);
    if (hash) user[key] = hash;
  }

  const country = (input.country ?? '').trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(country)) user.country = country;

  const city = (input.city ?? '').trim().slice(0, 128);
  if (city) user.city = city;

  const region = (input.region ?? '').trim().slice(0, 128);
  if (region) user.region = region;

  const postal = (input.postalCode ?? '').trim();
  if (/^[A-Za-z0-9 -]{1,32}$/.test(postal)) user.postal_code = postal;

  return user;
};

let consentGranted: boolean | null = null;
let currentUser: PixelUser = {};
let lastSent = '';
let epoch = 0;
let chain: Promise<void> = Promise.resolve();

const isConsentGranted = () =>
  consentGranted !== null ? consentGranted : isStoredConsentGranted();

const fingerprint = (user: PixelUser) =>
  JSON.stringify(
    Object.keys(user)
      .sort()
      .map((k) => [k, user[k]]),
  );

const sanitize = (raw: unknown): PixelUser => {
  const out: PixelUser = {};
  if (!raw || typeof raw !== 'object') return out;
  const obj = raw as Record<string, unknown>;

  for (const key of HASH_KEYS) {
    const value = obj[key];
    if (typeof value === 'string' && /^[a-f0-9]{64}$/.test(value))
      out[key] = value;
  }

  for (const key of PLAIN_KEYS) {
    const value = obj[key];
    if (typeof value === 'string' && value && value.length <= 128)
      out[key] = value;
  }

  return out;
};

const persist = () => {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(currentUser),
    );
  } catch {}
};

const forget = () => {
  epoch += 1;
  currentUser = {};
  lastSent = '';

  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
};

const flush = () => {
  if (!isConsentGranted()) return;
  if (Object.keys(currentUser).length === 0) return;
  if (!config.openaiAdsPixelId) return;

  const key = fingerprint(currentUser);
  if (key === lastSent) return;
  lastSent = key;

  persist();
  enqueueOaiq('init', {
    pixelId: config.openaiAdsPixelId,
    user: { ...currentUser },
  });
};

export const restoreOpenAIUser = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw)
      currentUser = { ...sanitize(JSON.parse(raw)), ...currentUser };
  } catch {}
  flush();
};

export const setOpenAIConsentState = (granted: boolean) => {
  consentGranted = granted;
  if (granted) {
    restoreOpenAIUser();
  } else {
    forget();
  }
};

export const identifyOpenAIUser = async (
  input: OpenAIUserInput,
): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (consentGranted === false) return Promise.resolve();

  const myEpoch = epoch;
  chain = chain.then(async () => {
    try {
      const next = await buildUser(input);
      if (myEpoch !== epoch) return;
      if (Object.keys(next).length === 0) return;
      currentUser = { ...currentUser, ...next };
      flush();
    } catch {}
  });
  return chain;
};

export const clearOpenAIUser = () => forget();
